import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_QAS, URL_QAS } from "@/constants/api_endpoints";
import { ICleanDocProps, IQasCleanDocState, IResponseCleanDocResult, IResponseCleanDocStatus } from "@/types/qas/qasCleanDocTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const initialState: IQasCleanDocState = {
	cleanDocStart: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		autocleaning: { status: false, promptId: undefined },
	},
	cleanDocStop: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	cleanDocStatus: {
		requestStatus: RequestStatus.IDLE,
		error: "",
		responseStatus: "stopped",
		progress: 0,
		queue: false,
		place: 0,
	},
	cleanDocResult: {
		status: RequestStatus.IDLE,
		data: null,
		docId: null,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// очистка документа от примесей
export const startCleanDoc = createAsyncThunk("qasCleanDoc/start", async ({ text, modelGpt, promptTemplateId, docId }: ICleanDocProps, { rejectWithValue, getState }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.CLEAN_DOC_START}`, {
			text,
			user_id: (getState() as RootState).user.currentUserInfo.id,
			model: modelGpt,
			template: promptTemplateId,
			doc_id: docId,
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// остановка очистки документа от примесей
export const stopCleanDoc = createAsyncThunk("qasCleanDoc/stop", async (_, { rejectWithValue, getState }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.CLEAN_DOC_STOP}`, {
			params: {
				user_id: (getState() as RootState).user.currentUserInfo.id,
			},
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// статус очистки документа от примесей
export const getStatusCleanDoc = createAsyncThunk(
	"qasCleanDoc/status",
	async (_, { rejectWithValue, getState }) => {
		try {
			const response: AxiosResponse<IResponseCleanDocStatus> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.CLEAN_DOC_STATUS}`, {
				params: {
					user_id: (getState() as RootState).user.currentUserInfo.id,
				},
			});
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	},
	{
		condition: (_, { getState }) => {
			if ((getState() as RootState).qasCleanDoc.cleanDocStatus.requestStatus === RequestStatus.LOADING) return false;
		},
	}
);

// результат очистки документа от примесей
export const getCleanDocResult = createAsyncThunk("qasCleanDoc/result", async (_, { rejectWithValue, getState }) => {
	try {
		const response: AxiosResponse<IResponseCleanDocResult> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.CLEAN_DOC_RESULT}`, {
			params: {
				user_id: (getState() as RootState).user.currentUserInfo.id,
			},
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const qasCleanDocSlice = createSlice({
	name: "qasCleanDoc",
	initialState,
	reducers: {
		// установка запуска автоочистки текста после загрузки документа
		setDocumentAutocleaningAfterLoading: (state, action: PayloadAction<{ status: boolean; promptId?: string }>) => {
			state.cleanDocStart.autocleaning = { status: action.payload.status, promptId: action.payload.promptId };
		},
		// очистка state
		clearState: (state) => {
			state.cleanDocStart = initialState.cleanDocStart;
			state.cleanDocStop = initialState.cleanDocStop;
			state.cleanDocStatus = initialState.cleanDocStatus;
			state.cleanDocResult = initialState.cleanDocResult;
		},
		// очистка статуса старта очистки документа
		clearCleanDocStart: (state) => {
			state.cleanDocStart = initialState.cleanDocStart;
		},
		// очистка статуса остановки очистки документа
		clearCleanDocStop: (state) => {
			state.cleanDocStop = initialState.cleanDocStop;
		},
		// очистка статуса процесса очистки документа
		clearCleanDocStatus: (state) => {
			state.cleanDocStatus = initialState.cleanDocStatus;
		},
		// очистка результата очистки документа
		clearCleanDocResult: (state) => {
			state.cleanDocResult = initialState.cleanDocResult;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(startCleanDoc.pending, (state) => {
				state.cleanDocStart.status = RequestStatus.LOADING;
			})
			.addCase(startCleanDoc.fulfilled, (state, action) => {
				if (action.payload) {
					state.cleanDocStart.status = RequestStatus.IDLE;
					state.cleanDocStart.error = action.payload.error;
					state.cleanDocStart.message = action.payload.message;
				} else state.cleanDocStart.status = RequestStatus.FAILED;
			})
			.addCase(startCleanDoc.rejected, (state, action: PayloadAction<unknown>) => {
				state.cleanDocStart.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.cleanDocStart.error = error;
				state.cleanDocStart.message = message;
			})
			.addCase(stopCleanDoc.pending, (state) => {
				state.cleanDocStop.status = RequestStatus.LOADING;
			})
			.addCase(stopCleanDoc.fulfilled, (state, action) => {
				if (action.payload) {
					state.cleanDocStop.status = RequestStatus.IDLE;
					state.cleanDocStop.error = action.payload.error;
					state.cleanDocStop.message = action.payload.message;
				} else state.cleanDocStop.status = RequestStatus.FAILED;
			})
			.addCase(stopCleanDoc.rejected, (state, action: PayloadAction<unknown>) => {
				state.cleanDocStop.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.cleanDocStop.error = error;
				state.cleanDocStop.message = message;
			})
			.addCase(getStatusCleanDoc.pending, (state) => {
				state.cleanDocStatus.requestStatus = RequestStatus.LOADING;
			})
			.addCase(getStatusCleanDoc.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload !== "string") {
					state.cleanDocStatus.requestStatus = RequestStatus.IDLE;
					state.cleanDocStatus.error = action.payload.error;
					state.cleanDocStatus.responseStatus = action.payload.status;
					state.cleanDocStatus.progress = action.payload.progress; // прогресс
					state.cleanDocStatus.queue = action.payload.queue; // наличие очереди
					state.cleanDocStatus.place = action.payload.place; // номер очереди
				} else state.cleanDocStatus.requestStatus = RequestStatus.FAILED;
			})
			.addCase(getStatusCleanDoc.rejected, (state, action: PayloadAction<unknown>) => {
				state.cleanDocStatus.requestStatus = RequestStatus.FAILED;
				const { error } = getResponseError(action);
				state.cleanDocStatus.error = error.toString();
			})
			.addCase(getCleanDocResult.pending, (state) => {
				state.cleanDocResult.status = RequestStatus.LOADING;
			})
			.addCase(getCleanDocResult.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object") {
					state.cleanDocResult.status = RequestStatus.IDLE;
					if (action.payload.text) state.cleanDocResult.data = action.payload.text; // наличие данных очистки
					if (action.payload.doc_id) state.cleanDocResult.docId = action.payload.doc_id; // наличие документа
				} else state.cleanDocResult.status = RequestStatus.FAILED;
			})
			.addCase(getCleanDocResult.rejected, (state, action: PayloadAction<unknown>) => {
				state.cleanDocResult.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.cleanDocResult.error = error;
				state.cleanDocResult.message = message;
			});
	},
});

export const { setDocumentAutocleaningAfterLoading, clearState, clearCleanDocStart, clearCleanDocStop, clearCleanDocStatus, clearCleanDocResult } =
	qasCleanDocSlice.actions;

export const selectCleanDocStart = (state: RootState) => state.qasCleanDoc.cleanDocStart;
export const selectCleanDocStop = (state: RootState) => state.qasCleanDoc.cleanDocStop;
export const selectCleanDocStatus = (state: RootState) => state.qasCleanDoc.cleanDocStatus;
export const selectCleanDocResult = (state: RootState) => state.qasCleanDoc.cleanDocResult;

export default qasCleanDocSlice.reducer;
